import { ResponsivePie } from '@nivo/pie';
import { ActivityEventProcessType } from '@warebee/shared/import-converter';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Stat } from '../../components/stats/Stat';
import { StatGroup } from '../../components/stats/StatGroup';
import { StatListItem } from '../../components/stats/StatListItem';
import useActivityEventProcessTypeDetails from '../../import/activityFeed/schema/useActivityEventProcessTypeDetails';
import ContainerChart from '../../simulation/panels/ContainerChart';
import * as KPI from '../kpi/categories';
import packingMockData from '../kpi/categories/packingMockData';
import pickingMockData from '../kpi/categories/pickingMockData';
import replenishmentMockData from '../kpi/categories/replenishmentMockData';
import KpiCard from '../kpi/helpers/KpiCard';
import {
  HiddenCostMetrics,
  PicklistMetrics,
  ProcessCostProfile,
} from '../types/metrics';

interface WarehouseDashboardProcessProps {
  processCosts: ProcessCostProfile[];
  hiddenCosts: HiddenCostMetrics;
  picklistMetrics: PicklistMetrics;
  timeRange: string;
}

const ProcessCostCard: React.FC<{ process: ProcessCostProfile }> = ({
  process,
}) => {
  const { t } = useTranslation('app');

  const pieData = process.activityCosts.map(cost => ({
    id: cost.activity,
    label: t(cost.activity),
    value: cost.laborCost + cost.equipmentCost + cost.overheadCost,
    color: `hsl(${Math.random() * 360}, 70%, 50%)`,
  }));

  return (
    <Stat
      hasHelper
      isPreview
      title={t('Process Cost Distribution')}
      value={process.totalCost.toString()}
      unitOfMeasure="$"
    >
      <ContainerChart containerHeight={200}>
        <ResponsivePie
          data={pieData}
          margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
          innerRadius={0.5}
          padAngle={0.7}
          cornerRadius={3}
          colors={{ scheme: 'nivo' }}
          borderWidth={1}
          borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
          enableArcLinkLabels={false}
          arcLabelsSkipAngle={10}
          arcLabelsTextColor="#ffffff"
        />
      </ContainerChart>
      <StatListItem
        title={t('Efficiency')}
        value={process.efficiency}
        unitOfMeasure="%"
      />
    </Stat>
  );
};

const HiddenCostsCard: React.FC<{ metrics: HiddenCostMetrics }> = ({
  metrics,
}) => {
  const { t } = useTranslation('app');

  return (
    <Stat
      hasHelper
      isPreview
      title={t('Hidden Process Costs')}
      value={metrics.rearrangementCost.toString()}
      unitOfMeasure="$"
    >
      <StatListItem
        title={t('Stock Rearrangements')}
        value={metrics.stockRearrangements}
      />
      <StatListItem
        title={t('Multi-Touch Count')}
        value={metrics.multiTouchCount}
      />
      <StatListItem
        title={t('Avg Touches/Item')}
        value={metrics.avgTouchesPerItem}
      />
      <StatListItem
        title={t('Rearrangement Time')}
        value={metrics.rearrangementTime}
        unitOfMeasure="h"
      />
    </Stat>
  );
};

const ProcessMetricsCard: React.FC<{ metrics: PicklistMetrics }> = ({
  metrics,
}) => {
  const { t } = useTranslation('app');

  return (
    <Stat
      hasHelper
      isPreview
      title={t('Process Metrics')}
      value={metrics.pickRate.toString()}
      unitOfMeasure="/hr"
    >
      <StatListItem
        title={t('Avg Lines/Picklist')}
        value={metrics.avgLinesPerPicklist}
      />
      <StatListItem
        title={t('Avg Lines/Order')}
        value={metrics.avgLinesPerOrder}
      />
      <StatListItem
        title={t('Avg Distance/Order')}
        value={metrics.avgDistancePerOrder}
        unitOfMeasure="m"
      />
      <StatListItem
        title={t('Avg Time/Order')}
        value={metrics.avgTimePerOrder}
        unitOfMeasure="min"
      />
    </Stat>
  );
};

function getMockDataForKpi(kpi) {
  if (pickingMockData[kpi.id]) return pickingMockData[kpi.id];
  if (packingMockData[kpi.id]) return packingMockData[kpi.id];
  if (replenishmentMockData[kpi.id]) return replenishmentMockData[kpi.id];
  // ...etc
}

const WarehouseDashboardProcess: React.FC<WarehouseDashboardProcessProps> = ({
  processCosts,
  hiddenCosts,
  picklistMetrics,
  timeRange,
}) => {
  const { t } = useTranslation('app');
  const currentProcess = processCosts[0]; // Using first process for example
  const processDetails = useActivityEventProcessTypeDetails();
  // Helper: flatten all KPIs from all categories
  const allKpis = [
    ...KPI.pickingKpis,
    ...KPI.replenishmentKpis,
    ...KPI.packingKpis,
    ...KPI.putawayKpis,
    // ...KPI.storageKpis,
    ...KPI.shippingKpis,
    ...KPI.returnsKpis,
    ...KPI.maintenanceKpis,
    ...KPI.allocateKpis,
    ...KPI.receivingKpis,
    ...KPI.crossdockingKpis,
    ...KPI.loadingKpis,
    ...KPI.slottingKpis,
    // ...KPI.reassignmentKpis,
    ...KPI.unloadingKpis,
    ...KPI.processKpis,
    ...KPI.inventoryKpis,
    ...KPI.issuesKpis,
    ...KPI.labourKpis,
    ...KPI.clientsKpis,
    ...KPI.complianceKpis,
    ...KPI.dataIoKpis,
    ...KPI.insightsKpis,
    ...KPI.warehouseSummaryKpis,
    ...KPI.sortingKpis,
    ...KPI.stagingKpis,
    ...KPI.stackingKpis,
    ...KPI.destackingKpis,
    ...KPI.kittingKpis,
    ...KPI.repairKpis,
    ...KPI.identificationKpis,
    ...KPI.systemKpis,
    ...KPI.transportKpis,
    ...KPI.inventoryCountKpis,
    ...KPI.valueAddKpis,
    ...KPI.moveKpis,
    ...KPI.managementKpis,
  ];

  // Group KPIs by processType
  const kpisByProcess: Record<ActivityEventProcessType, typeof allKpis> =
    allKpis.reduce(
      (acc, kpi) => {
        if (!kpi.processType) return acc;
        const types = Array.isArray(kpi.processType)
          ? kpi.processType
          : [kpi.processType];
        types.forEach(type => {
          if (!acc[type]) acc[type] = [];
          acc[type].push(kpi);
        });
        return acc;
      },
      {} as Record<ActivityEventProcessType, typeof allKpis>,
    );

  return (
    <StatGroup title={t('Process Dashboard')} className="p-4">
      {/* <div className="mb-4 flex items-center justify-between">
        <span className="text-gray-400">{timeRange}</span>
      </div> */}

      <div className="grid grid-cols-1 gap-1 md:grid-cols-2 lg:grid-cols-3">
        <ProcessCostCard process={currentProcess} />
        <HiddenCostsCard metrics={hiddenCosts} />
        <ProcessMetricsCard metrics={picklistMetrics} />
      </div>
      <div data-component="WarehouseDashboardProcess" className="grid gap-1">
        {processDetails.map(process => {
          const numKpis = (kpisByProcess[process.id] || []).length;
          const gridCols = `grid-cols-${Math.min(numKpis, 4)}`;
          return (
            <StatGroup
              title={
                <div className="text-menu-text">
                  <div>{process.title}</div>
                  <p className="text-sm opacity-50">{process.description}</p>
                </div>
              }
              key={process.id}
              classNameStatGroupItems={`grid items-stretch w-full p-fix flex-col !gap-1 justify-stretch content-stretch !space-x-none !space-y-0 ${gridCols}`}
            >
              {(kpisByProcess[process.id] || []).map(kpi => {
                const isPrimary = kpi.isPrimary;
                // Responsive: On md+ screens, primary takes 2 columns, others 1
                const colSpan = isPrimary
                  ? 'col-span-2 md:col-span-4 lg:col-span-3 xl:col-span-2' // adjust as needed
                  : 'col-span-1';

                return (
                  <div key={kpi.id} className={`h-full ${colSpan}`}>
                    <KpiCard
                      kpi={kpi}
                      value={getMockDataForKpi(kpi)?.current}
                      trendData={getMockDataForKpi(kpi)?.trendData}
                      trend={getMockDataForKpi(kpi)?.trend}
                      status={getMockDataForKpi(kpi)?.status}
                    />
                  </div>
                );
              })}
            </StatGroup>
          );
        })}
      </div>
    </StatGroup>
  );
};

export default WarehouseDashboardProcess;
